import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";

export default function TermsOfUsePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-10 mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Terms of Use</h1>
          <p className="text-slate-500">Last Updated: March 20, 2026</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-4">
              Welcome to GigsEasy.com ("GigsEasy," "we," "us," or "our"), a service owned and operated by <strong>Active Consumers LLC</strong>. 
              These Terms of Use ("Terms") govern your access to and use of GigsEasy's website, applications, and services 
              (collectively, the "Service"). Please read these Terms carefully before using our Service.
            </p>
            <p className="text-slate-600 mb-4">
              By accessing or using the Service, you agree to be bound by these Terms and our{" "}
              <Link to="/privacy-policy" className="text-[#00B894] hover:underline">Privacy Policy</Link>. If you do not agree to 
              these Terms, you may not access or use the Service. We reserve the right to modify these Terms at any time, and such 
              modifications shall be effective immediately upon posting on the Service.
            </p>
            <p className="text-slate-600">
              Your continued use of the Service after any modifications indicates your acceptance of the modified Terms. 
              We encourage you to review these Terms periodically for any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Eligibility</h2>
            <p className="text-slate-600 mb-4">
              You must be at least 18 years of age to use the Service. By using the Service, you represent and warrant that you are 
              at least 18 years old and have the legal capacity to enter into these Terms. If you are using the Service on behalf 
              of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Account Registration and Security</h2>
            <p className="text-slate-600 mb-4">
              To access certain features of the Service, you must register for an account. When registering, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security and confidentiality of your login credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Immediately notify us of any unauthorized use of your account</li>
              <li>Register up to 10 email addresses with your account for task purposes</li>
            </ul>
            <p className="text-slate-600">
              We reserve the right to suspend or terminate your account if any information provided proves to be inaccurate, 
              outdated, or incomplete, or if we suspect fraudulent or unauthorized activity.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Points System and Payments</h2>
            <p className="text-slate-600 mb-4">
              GigsEasy operates a points-based reward system. Points are earned by completing tasks and can be converted to USD 
              for withdrawal. Key terms:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Points have no cash value until withdrawn</li>
              <li>Minimum withdrawal thresholds may apply</li>
              <li>Withdrawals are processed via PayPal</li>
              <li>Processing times vary by payment method</li>
              <li>You are responsible for any applicable taxes on earnings</li>
            </ul>
            <p className="text-slate-600">
              We reserve the right to modify point values, conversion rates, and withdrawal terms at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Acceptable Use Policy</h2>
            <p className="text-slate-600 mb-4">You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Provide false, inaccurate, or misleading information</li>
              <li>Create more than one account per person</li>
              <li>Transfer, sell, or share your account with any other person</li>
              <li>Use any robot, spider, scraper, or other automated means to access the Service</li>
              <li>Use proxy servers, VPNs, or similar technologies to mask your location or identity</li>
              <li>Submit the same task multiple times using different accounts</li>
              <li>Attempt to manipulate, cheat, or exploit the reward system</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Engage in any illegal, fraudulent, or malicious activity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Communications and Messaging Consent (TCPA)</h2>
            <p className="text-slate-600 mb-4">
              By creating an account and providing your contact information, you consent to receive communications from 
              GigsEasy.com and Active Consumers LLC as follows:
            </p>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3 mt-6">6.1 Transactional Communications</h3>
            <p className="text-slate-600 mb-4">
              You consent to receive transactional messages related to your account, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Account creation confirmations and verification emails</li>
              <li>Task notifications and assignments</li>
              <li>Payment and withdrawal confirmations</li>
              <li>Account status updates and security alerts</li>
              <li>Service announcements and important notices</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3 mt-6">6.2 Marketing Communications</h3>
            <p className="text-slate-600 mb-4">
              With your separate opt-in consent, you may receive marketing communications including promotional offers, 
              new feature announcements, and other updates. Marketing consent is optional and not a condition for using 
              our services.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3 mt-6">6.3 Message Terms</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Message and data rates may apply</li>
              <li>You may receive up to 4 messages per month</li>
              <li>You can opt out at any time by contacting support@gigseasy.com</li>
              <li>Carriers are not responsible for delayed or undelivered messages</li>
              <li>All information collected during the opt-in process will not be sold, rented, or shared with third parties for marketing purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3 mt-6">6.4 Consent Withdrawal</h3>
            <p className="text-slate-600 mb-4">
              You may withdraw your consent to receive marketing communications at any time by clicking "unsubscribe" in any 
              email or by contacting us at support@gigseasy.com. Note that you cannot opt out of transactional messages 
              necessary for service delivery while maintaining an active account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Intellectual Property Rights</h2>
            <p className="text-slate-600 mb-4">
              The Service and its original content, features, and functionality are owned by Active Consumers LLC and are 
              protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. 
              Our trademarks, service marks, and logos may not be used without our prior written consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Third-Party Services</h2>
            <p className="text-slate-600 mb-4">
              The Service may integrate with or contain links to third-party services, including payment processors, 
              email providers, and analytics services. Your use of such third-party services is subject to their respective 
              terms and privacy policies. We are not responsible for the content, privacy practices, or availability of 
              third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-slate-600 mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
              NON-INFRINGEMENT, AND TITLE.
            </p>
            <p className="text-slate-600">
              We do not warrant that the Service will be uninterrupted, error-free, secure, or free of viruses or other 
              harmful components. We do not guarantee task availability, earning potential, or that communications will 
              be delivered on time or at all.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ACTIVE CONSUMERS LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS 
              SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT 
              LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p className="text-slate-600">
              In no event shall our total liability exceed the amount you earned through the Service, if any, during the 
              twelve (12) months preceding the claim. Some jurisdictions do not allow the exclusion or limitation of certain 
              damages, so some of the above limitations may not apply to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Indemnification</h2>
            <p className="text-slate-600">
              You agree to indemnify, defend, and hold harmless Active Consumers LLC and its officers, directors, employees, 
              agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses, including 
              reasonable attorneys' fees, arising out of or in any way connected with your access to or use of the Service, 
              your violation of these Terms, or your violation of any third-party rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Termination</h2>
            <p className="text-slate-600 mb-4">
              We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any 
              reason, including but not limited to a breach of these Terms. Upon termination, your right to use the Service 
              will immediately cease.
            </p>
            <p className="text-slate-600">
              You may terminate your account at any time through your account settings or by contacting our support team. 
              Upon termination, we may delete your data in accordance with our data retention policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Governing Law and Dispute Resolution</h2>
            <p className="text-slate-600">
              These Terms shall be governed by and construed in accordance with the laws of Puerto Rico, United States, 
              without regard to its conflict of law provisions. Any disputes arising from these Terms or the Service shall 
              be resolved through binding arbitration in accordance with the American Arbitration Association's rules, 
              conducted in Puerto Rico.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">14. Severability</h2>
            <p className="text-slate-600">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or 
              eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">15. Entire Agreement</h2>
            <p className="text-slate-600">
              These Terms, together with our{" "}
              <Link to="/privacy-policy" className="text-[#00B894] hover:underline">Privacy Policy</Link> and{" "}
              <Link to="/california-privacy" className="text-[#00B894] hover:underline">California Privacy Notice</Link>, 
              constitute the entire agreement between you and Active Consumers LLC regarding the Service and supersede all 
              prior agreements, understandings, and communications, whether written or oral.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">16. Contact Information</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="font-semibold text-slate-900">Active Consumers LLC</p>
              <p className="text-slate-600">San Juan, Puerto Rico 00901</p>
              <p className="text-slate-600">Email: support@gigseasy.com</p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
